import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext
} from "react";

type ProductData = {
    readonly productKey: string;
    readonly productName: string;
    readonly productLogoUrl?: string;
    readonly productIconUrl?: string;
};

type ProductDataType = {
    readonly [index: string]: ProductData;
};

type BundleMapType = {
    readonly [index: string]: string;
};

/**
 * BundleProvisioningStatusType
 * waiting - initial/idle value on load
 * payload-ready - set by Bundles UI, means user has selected the Next button,
 *                 and that the Confirmation page can hide the Bundles UI
 * provisioning - set by confirmation page, means that the confirmation page
 *                has detected that provisioning an initial site is successful
 * success - set by bundles UI, means that provisioning secondary products is successful,
 *           and the confirmation page is free to continue (eg. redirect)
 * no-payload - used for the video modal to indicate user has closed before video ended
 */
export type BundleProvisioningStatusType =
    | "waiting"
    | "payload-ready"
    | "provisioning"
    | "success"
    | "no-payload";

type FreeBundlesStatusProviderProps = {
    readonly bundleProvisioningStatus: BundleProvisioningStatusType;
    readonly setBundleProvisioningStatus: Dispatch<
        SetStateAction<BundleProvisioningStatusType>
    >;
    readonly bundleMap: BundleMapType;
    readonly productData: ProductDataType;
};

export function useFreeBundlesStatus() {
    return useContext(FreeBundlesStatusContext);
}

const FreeBundlesStatusContext = createContext<FreeBundlesStatusProviderProps>({
    bundleProvisioningStatus: "waiting",
    setBundleProvisioningStatus: () => undefined,
    bundleMap: {},
    productData: {}
});

export const FreeBundlesStatusProvider = ({
    bundleProvisioningStatus = "waiting",
    setBundleProvisioningStatus = () => undefined,
    bundleMap = {},
    productData = {},
    children
}: PropsWithChildren<FreeBundlesStatusProviderProps>) => {
    return (
        <FreeBundlesStatusContext.Provider
            value={{
                bundleProvisioningStatus,
                setBundleProvisioningStatus,
                bundleMap,
                productData
            }}
        >
            {children}
        </FreeBundlesStatusContext.Provider>
    );
};
